/* eslint-disable @typescript-eslint/naming-convention */
import postHog from 'posthog-js';
import {watch, type WatchStopHandle} from 'vue';
import type {UserInfoData} from '@/ts/types/posthog';
import type {Language} from '@/ts/types/component/localization.type';
import {loggerInstance} from '@/ts/instances/logger-instance';
import {POSTHOG} from '@/ts/utils/consts';
import {useUserStore} from '@/ts/store/user-store';
import {shouldEnableAnalytics} from '@/ts/utils/analytics-consent';

// Taken from init() so posthog-js keeps a single default import the medical-device analyzer can read.
type PostHogConfig = NonNullable<Parameters<typeof postHog.init>[1]>;

/**
 * Which bundle runs analytics. The patient app keeps autocapture and pageviews;
 * the admin dashboard only records which of its static routes were visited.
 */
type AnalyticsProfile = 'admin' | 'main';

/**
 * Person properties that older builds set on PostHog (email until Sept 2025).
 * Demographics now live in the backend only (IPD-3608), so identify removes them
 * from persons that still carry them.
 */
const REMOVED_PERSON_PROPERTIES = ['name', 'email', 'birthYear', 'educationLevel', 'gender'];
const REMOVED_PROPERTIES_MARKER = 'analytics-demographics-unset';

/**
 * Settings shared by both bundles, one code path for the EU and US projects.
 * No text or element attributes from the page (aria-labels carry user input such
 * as journal feelings), no session recording, no exception or dead-click capture
 * (Sentry owns errors). The remote project setting cannot start a recording while
 * `disable_session_recording` is set; the masks only cover an explicit
 * `startSessionRecording()` call.
 */
const BASE_CONFIG = {
  persistence: 'localStorage',
  capture_pageview: 'history_change',
  mask_all_text: true,
  mask_all_element_attributes: true,
  disable_session_recording: true,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: '*',
  },
  enable_recording_console_log: false,
  capture_exceptions: false,
  capture_dead_clicks: false,
} as const satisfies Partial<PostHogConfig>;

/** Admin: pageviews of its static routes only, for support and debugging. */
const ADMIN_CONFIG = {
  ...BASE_CONFIG,
  autocapture: false,
  capture_pageleave: false,
  capture_heatmaps: false,
  capture_performance: false,
  disable_surveys: true,
} as const satisfies Partial<PostHogConfig>;

/** Manages PostHog analytics initialization, user identification, and event tracking. */
export class AnalyticsService {
  private readonly logger = loggerInstance.getLogger('analytics');
  private initialized = false;
  private profile: AnalyticsProfile | null = null;
  private stopWatcher?: WatchStopHandle;

  // Check if analytics should be enabled based on user opt-in and analytics anonymized consent
  private isAnalyticsEnabled(): boolean {
    const userStore = useUserStore();
    return shouldEnableAnalytics(userStore.userData?.consents);
  }

  // Check if analytics is ready to be used (enabled + initialized)
  private isReady(): boolean {
    return this.isAnalyticsEnabled() && this.initialized;
  }

  /**
   * Starts watching the signed-in user. Each bundle calls it once from its entry
   * point; a bundle that never calls it never loads PostHog.
   */
  start(profile: AnalyticsProfile): void {
    if (this.profile) {
      return;
    }
    this.profile = profile;
    // Don't initialize PostHog immediately - wait for user authentication
    // Defer watcher setup to avoid Pinia initialization issues
    this.deferredWatcherSetup();
  }

  private deferredWatcherSetup(): void {
    // Use nextTick to ensure Pinia is ready before setting up the watcher
    void import('vue').then(({nextTick}) => {
      void nextTick(() => {
        this.watchAuthState();
      });
    });
  }

  private watchAuthState(): void {
    const userStore = useUserStore();
    this.stopWatcher = watch(
      () => userStore.userData,
      (userData, previousUserData) => {
        if (userData && !this.initialized) {
          // User logged in for the first time - initialize PostHog once
          this.initializePosthog();
          this.identifyUser(userData);
        } else if (userData && this.initialized) {
          // User logged in but PostHog already initialized - just identify
          this.identifyUser(userData);
        } else if (!userData && previousUserData && this.initialized) {
          // User logged out - reset PostHog session
          this.resetUserSession();
        }
      },
      {immediate: true},
    );
  }

  private initializePosthog(): void {
    if (this.initialized || !this.profile || !POSTHOG?.API_KEY || !POSTHOG?.HOST) {
      return;
    }

    postHog.init(POSTHOG.API_KEY, {
      ...(this.profile === 'admin' ? ADMIN_CONFIG : BASE_CONFIG),
      api_host: POSTHOG.HOST,
    });

    this.initialized = true;

    // Mirror the BE consent in BOTH directions. The explicit opt-in heals a
    // stale opt-out persisted on this device after the consent was re-approved
    // elsewhere — since IPD-2899 the BE consents are the only source of truth
    // (the cookie banner that used to reconcile this on login is gone).
    // Guarded on a real opt-out→opt-in transition: opt_in_capturing() always
    // emits an instant $opt_in event, so an unconditional call would fire it
    // on every page load.
    const userStore = useUserStore();
    const userAnalyticsOptIn = shouldEnableAnalytics(userStore.userData?.consents);
    if (userAnalyticsOptIn) {
      if (postHog.has_opted_out_capturing()) {
        postHog.opt_in_capturing();
      }
      this.logger.debug('PostHog initialized after user authentication')();
    } else {
      postHog.opt_out_capturing();
      this.logger.debug('PostHog initialized but user opted out - capturing disabled')();
    }
  }

  /**
   * Clears the local PostHog session (distinct_id, session_id, survey-shown flags,
   * feature-flag cache). Called by the userData watcher on in-app logout, and
   * directly by the Zitadel sign-out path before the full-page redirect destroys
   * the SPA — without that, the next user inherits the previous user's
   * distinct_id and survey-dedup state.
   */
  resetUserSession(): void {
    if (!this.initialized) {
      return;
    }

    if (!postHog.has_opted_out_capturing()) {
      postHog.capture('user_logout', {}, {send_instantly: true});
    }
    postHog.reset();
  }

  /** Identifies the user with the marketing code and registration date only; demographics stay in the backend. */
  identifyUser(userData: UserInfoData | null): void {
    if (!this.isReady() || !userData) {
      return;
    }

    postHog.identify(userData.id, {
      marketingCode: userData.marketingCodeExternalId,
      registrationDate: userData.registrationDate,
    });
    this.unsetRemovedProperties(userData.id);

    this.logger.debug('User identified:', userData.id)();
  }

  /**
   * Removes the demographics older builds set on this person. A per-user marker
   * keeps it to one `$set` event per sign-in (logout clears localStorage), not one
   * per page load.
   */
  private unsetRemovedProperties(userId: string): void {
    const marker = `${REMOVED_PROPERTIES_MARKER}:${userId}`;
    try {
      if (localStorage.getItem(marker)) {
        return;
      }
      postHog.unsetPersonProperties(REMOVED_PERSON_PROPERTIES);
      localStorage.setItem(marker, '1');
    } catch (error) {
      this.logger.warn('Could not unset removed person properties', error)();
    }
  }

  // Update user properties
  updateUserProperties(properties: Record<string, any>): void {
    if (!this.isReady()) {
      return;
    }

    postHog.setPersonProperties(properties);
    this.logger.debug('User properties updated:', properties)();
  }

  // Set user language preference
  setLanguage(language: Language): void {
    if (!this.isReady()) {
      return;
    }

    postHog.setPersonProperties({preferredLanguage: language});
    this.logger.debug('Language preference set:', language)();
  }

  // Track events with properties
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.isReady()) {
      return;
    }

    postHog.capture(eventName, properties);
    this.logger.debug('Event tracked:', eventName, 'with properties', properties)();
  }

  // Check if feature flag is enabled
  isFeatureEnabled(flagName: string, defaultValue = false): boolean {
    if (!this.isReady()) {
      return defaultValue;
    }

    return postHog.isFeatureEnabled(flagName) ?? defaultValue;
  }

  setAlias(alias: string, original: string): void {
    if (!this.isReady()) {
      return;
    }

    postHog.alias(alias, original);
    this.logger.debug('Alias set:', alias, 'for', original)();
  }

  // Full reset including device ID (public API)
  reset(): void {
    if (!this.isReady()) {
      return;
    }

    postHog.reset(true);
    this.logger.debug('Analytics full reset with device ID')();
  }

  // Opt in of capturing data
  optInCapturing(): void {
    if (!this.initialized) {
      return;
    }

    postHog.opt_in_capturing();

    // Re-identify the user when opting back in to ensure proper tracking
    const userStore = useUserStore();
    if (userStore.userData) {
      this.identifyUser(userStore.userData);
    }

    this.logger.debug('PostHog capturing opted in and user re-identified')();
  }

  // Opt out of capturing data
  optOutCapturing(): void {
    if (!this.initialized) {
      return;
    }

    postHog.opt_out_capturing();
    this.logger.debug('PostHog capturing opted out')();
  }
}

/** Singleton PostHog analytics instance that initializes on user authentication and respects consent opt-in */
export const analyticsService = new AnalyticsService();

export type {AnalyticsProfile};
